const CookiePolicy = () => (
  <div className="flex flex-col gap-2 text-sm font-inter">
    <h2 className="mb-2">Cookie Policy for Candidates and Companies</h2>
    <div className="px-0 md:px-6">
      <p>
        <strong>Effective Date:</strong> May 2, 2025
      </p>
      <p className="mt-4">
        At Qureos Inc. ("Qureos," "we," "our," or "us"), we believe in being
        transparent about how we collect and use data related to individuals and
        companies who interact with our platform. This Cookie Policy provides
        detailed information about what cookies are, how we use them, why we use
        them, and how you can manage your cookie preferences. Whether you are a
        Candidate or a Company representative, we are committed to protecting
        your privacy and enhancing your experience.
      </p>
      <p className="mt-4">
        By continuing to use our website or mobile applications, you agree to
        the placement and use of cookies as described in this Policy.
      </p>
    </div>
    <h3 className="mt-8">Definitions</h3>
    <div className="px-0 md:px-6">
      <p>
        To ensure clarity in this Policy, we define key terms used throughout:
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>"Candidate"</strong>: An individual who uses Qureos to explore
          career opportunities or participate in assessments.
          <ul className="list-disc pl-5 mt-2">
            <li>
              Candidates may encounter cookies related to personalization, job
              recommendations, and performance tracking.
            </li>
          </ul>
        </li>
        <li className="mb-4">
          <strong>"Company"</strong>: A business entity or representative using
          Qureos to post jobs, recruit, or manage applicants.
          <ul className="list-disc pl-5 mt-2">
            <li>
              Company users experience cookies that facilitate candidate
              searches and subscription management.
            </li>
          </ul>
        </li>
        <li className="mb-4">
          <strong>"Platform"</strong>: Refers to all Qureos websites, mobile
          apps, and digital services.
          <ul className="list-disc pl-5 mt-2">
            <li>
              The platform uses cookies to maintain seamless functionality and
              security.
            </li>
          </ul>
        </li>
        <li className="mb-4">
          <strong>"Cookies"</strong>: Small text files placed on your device
          that collect information to enhance user experience and ensure
          efficient platform operation.
          <ul className="list-disc pl-5 mt-2">
            <li>
              Cookies may store login details, preferences, or anonymized
              tracking data.
            </li>
          </ul>
        </li>
      </ul>
      <p>
        Providing these definitions ensures that both Candidates and Companies
        understand the specific meaning of terms used herein.
      </p>
    </div>

    <h3 className="mt-8">Scope</h3>
    <div className="px-0 md:px-6">
      <p>
        This Cookie Policy applies to all users who visit or interact with the
        Qureos platform, including Candidates seeking jobs and Companies
        managing recruitment.
      </p>
      <p className="mt-4">
        CandCookies may be set by us (first-party cookies) or by third-party
        providers (third-party cookies). They operate across web browsers,
        mobile devices, and integrated services.
      </p>
      <p className="mt-4">
        Understanding the scope helps clarify that cookie usage affects all
        types of users interacting with the platform, regardless of the device
        used.
      </p>
    </div>
    <h3 className="mt-8">What Are Cookies?</h3>
    <div className="px-0 md:px-6">
      <p>
        Cookies are small files stored on your device when you visit a website.
        They serve several essential functions, such as remembering your login
        details, personalizing content, or tracking site usage statistics to
        improve services.
      </p>
      <ul className="list-disc pl-5 mt-4">
        <li className="mb-4">
          <strong>Session Cookies</strong>: These expire once you close your
          browser.
          <ul className="list-disc pl-5 mt-2">
            <li>
              They help with short-term processes, like staying logged in during
              a single session.
            </li>
          </ul>
        </li>
        <li className="mb-4">
          <strong>Persistent Cookies</strong>: These remain on your device for a
          set period or until you delete them.
          <ul className="list-disc pl-5 mt-2">
            <li>
              Persistent cookies enable features like remembering your
              preferences across sessions.
            </li>
          </ul>
        </li>
        <li className="mb-4">
          <strong>First-Party Cookies</strong>: Set by Qureos to maintain
          internal functionality.
          <ul className="list-disc pl-5 mt-2">
            <li>
              These cookies manage platform operations and user authentication.
            </li>
          </ul>
        </li>
        <li className="mb-4">
          <strong>Third-Party Cookies</strong>: Set by trusted partners like
          analytics or ad providers
          <ul className="list-disc pl-5 mt-2">
            <li>
              These cookies help us analyze traffic and deliver relevant ads.
            </li>
          </ul>
        </li>
      </ul>
      <p className="mt-4">
        Understanding cookies empowers users to make informed decisions about
        their privacy and platform usage.
      </p>
    </div>

    <h3 className="mt-8">Type of Cookies We Use</h3>
    <div className="px-0 md:px-6">
      <p>
        We categorize cookies based on their function and the impact they have
        on the user experience.
      </p>

      <h4 className="mt-4">4.1 Essential Cookies</h4>
      <p>
        Essential cookies are necessary for the operation of the Qureos
        platform. Without these cookies, basic services such as user
        authentication, secure login, and platform navigation cannot function.
      </p>
      <ul className="list-disc pl-5 mt-4">
        <li className="mb-4">
          <strong>Authentication Cookies</strong>: Keep you logged into your
          account securely.
          <ul className="list-disc pl-5 mt-2">
            <li>
              These are required for Candidates to apply for jobs and for
              Companies to access candidate profiles.
            </li>
          </ul>
        </li>
        <li className="mb-4">
          <strong>Security Cookies</strong>: Help detect fraud and protect user
          accounts.
          <ul className="list-disc pl-5 mt-2">
            <li>
              They ensure that user data remains confidential and platform
              integrity is preserved.
            </li>
          </ul>
        </li>
        <li className="mb-4">
          <strong>Session Management Cookies</strong>: Maintain your session
          information during your visit.
          <ul className="list-disc pl-5 mt-2">
            <li>They prevent disruptions during your browsing experience.</li>
          </ul>
        </li>
      </ul>
      <p>
        Essential cookies ensure the platform operates smoothly and securely,
        supporting critical user tasks.
      </p>

      <h4 className="mt-4 mb-2">4.2 Performance Cookies</h4>
      <p>
        Performance cookies allow us to monitor and improve the functionality of
        our Platform by analyzing how users interact with it.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Analytics Cookies</strong>: Track metrics such as page views,
          time on site, and user flows.
          <ul className="list-disc pl-5 mt-2">
            <li>
              These insights help us optimize navigation and platform usability.
            </li>
          </ul>
        </li>
        <li className="mb-4">
          <strong>Error Tracking Cookies</strong>: Identify bugs or crashes
          experienced by users.
          <ul className="list-disc pl-5 mt-2">
            <li>
              They assist our development team in maintaining system
              reliability.
            </li>
          </ul>
        </li>
        <li className="mb-4">
          <strong>Usage Pattern Cookies</strong>: Observe how different user
          segments use features.
          <ul className="list-disc pl-5 mt-2">
            <li>
              This data helps prioritize future improvements and feature
              expansions.
            </li>
          </ul>
        </li>
      </ul>
      <p>
        By analyzing performance data, we can enhance the overall platform
        experience for both Candidates and Companies.
      </p>

      <h4 className="mt-4 mb-2">4.3 Advertising and Targeting Cookies</h4>
      <p>
        These cookies are used to deliver relevant advertisements to users and
        measure the effectiveness of marketing campaigns.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Retargeting Cookies</strong>: Show personalized ads to users
          who have previously visited Qureos.
          <ul className="list-disc pl-5 mt-2">
            <li>
              They help reconnect Candidates with new opportunities and
              Companies with platform offerings.
            </li>
          </ul>
        </li>
        <li className="mb-4">
          <strong>Ad Performance Cookies</strong>: Measure the success of
          promotional campaigns.
          <ul className="list-disc pl-5 mt-2">
            <li>
              We use this information to improve our marketing efforts
              responsibly.
            </li>
          </ul>
        </li>
        <li className="mb-4">
          <strong>Social Media Cookies</strong>: Enable interaction with social
          media platforms (e.g., LinkedIn, Facebook).
          <ul className="list-disc pl-5 mt-2">
            <li>
              These cookies may track engagement with integrated social content.
            </li>
          </ul>
        </li>
      </ul>
      <p>
        Advertising cookies allow Qureos to maintain a sustainable platform
        while keeping ads relevant and nonintrusive.
      </p>
    </div>
    <h3 className="mt-8 mb-2">5. Third-Party Cookies</h3>
    <div className="px-0 md:px-6">
      <p>
        We partner with reputable third-party providers to enhance our platform
        functionality and support marketing efforts. These third parties may
        place cookies on your device.
      </p>
      <p className="mt-4">Examples include:</p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Google Analytics</strong>: Provides anonymized usage data to
          improve platform design.
        </li>
        <li className="mb-4">
          <strong>Meta (Facebook) Pixel</strong>: Measures the effectiveness of
          recruitment ads on social media.
        </li>
        <li className="mb-4">
          <strong>LinkedIn Insights Tag</strong>: Tracks interactions with job
          listings promoted on LinkedIn.
        </li>
      </ul>
      <p>
        Third-party cookies are subject to the privacy policies of the
        respective providers. We carefully vet all partners to ensure GDPR
        compliance and data protection standards.
      </p>
    </div>

    <h3 className="mt-8 mb-2">6. Managing Your Cookie Preferences</h3>
    <div className="px-0 md:px-6">
      <p>
        You have control over how cookies are used. Upon visiting the Qureos
        platform, you are presented with a cookie consent banner allowing you
        to:
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Accept All Cookies</strong>: Enable full functionality,
          personalization, and tracking features.
          <ul className="list-disc pl-5 mt-2">
            <li>Recommended for optimal platform experience.</li>
          </ul>
        </li>
        <li className="mb-4">
          <strong>Accept Only Essential Cookies</strong>: Restrict usage to only
          necessary cookies.
          <ul className="list-disc pl-5 mt-2">
            <li>
              This choice may limit certain features but ensures minimal data
              tracking.
            </li>
          </ul>
        </li>
        <li className="mb-4">
          <strong>Customize Settings</strong>: Adjust your cookie preferences
          through your browser or device settings.
          <ul className="list-disc pl-5 mt-2">
            <li>
              Most browsers allow you to clear or block cookies at any time.
            </li>
          </ul>
        </li>
      </ul>
      <p>
        Empowering users to manage their cookie preferences helps maintain
        transparency and enhance user trust.
      </p>
    </div>

    <h3 className="mt-8 mb-2">7. Changes to This Cookie Policy</h3>
    <div className="px-0 md:px-6">
      <p>
        We may update this Cookie Policy from time to time to reflect changes in
        law, technology, or our platform features. When material updates are
        made, we will notify users prominently.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Notification Methods</strong>: Changes will be communicated
          via email or platform banners.
        </li>
        <li className="mb-4">
          <strong>Effective Date</strong>: Updates take effect once published
          unless otherwise stated.
        </li>
        <li className="mb-4">
          <strong>Ongoing Review</strong>: We encourage users to review this
          Cookie Policy regularly.
        </li>
      </ul>
      <p>
        Your continued use of the platform after updates signifies acceptance of
        the revised policy.
      </p>
    </div>

    <h3 className="mt-8 mb-2">8. Contact Us</h3>
    <div className="px-0 md:px-6">
      <p>
        If you have any questions, concerns, or requests regarding our use of
        cookies, please feel free to contact us. We value your feedback and are
        committed to addressing your privacy inquiries promptly.
      </p>
      <p>
        <strong>Qureos Inc.</strong>
      </p>
      <ul className="list-disc pl-5">
        <li>
          Email:{' '}
          <a href="mailto:hello@qureos.com" className="text-qureosPrimary">
            hello@qureos.com
          </a>
        </li>
        <li>Jurisdiction: UAE and KSA</li>
      </ul>
    </div>
  </div>
)

export default CookiePolicy
